import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export interface TutorialCategory {
  id: string;
  name: {
    en: string;
    de: string;
    cz: string;
    sk: string;
  };
  slug: string;
  order: number;
  created_at: string;
  updated_at: string;
}

export interface Tutorial {
  id: string;
  category_id: string;
  title: {
    en: string;
    de: string;
    cz: string;
    sk: string;
  };
  description: {
    en: string;
    de: string;
    cz: string;
    sk: string;
  };
  video_url: string;
  thumbnail_url?: string;
  order: number;
  published: boolean;
  created_at: string;
  updated_at: string;
}

export function useSupabaseTutorialCategories() {
  const [categories, setCategories] = useState<TutorialCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('tutorial_categories')
        .select('*')
        .order('order', { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addCategory = async (category: Omit<TutorialCategory, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('tutorial_categories')
        .insert([category])
        .select()
        .single();

      if (error) throw error;
      await fetchCategories();
      return data;
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const updateCategory = async (id: string, updates: Partial<TutorialCategory>) => {
    try {
      const { error } = await supabase
        .from('tutorial_categories')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
      await fetchCategories();
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const deleteCategory = async (id: string) => {
    try {
      const { error } = await supabase
        .from('tutorial_categories')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await fetchCategories();
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  return {
    categories,
    loading,
    error,
    refetch: fetchCategories,
    addCategory,
    updateCategory,
    deleteCategory,
  };
}

export function useSupabaseTutorials(categoryId?: string) {
  const [tutorials, setTutorials] = useState<Tutorial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTutorials = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('tutorials')
        .select('*')
        .order('order', { ascending: true });

      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        query = query.eq('published', true);
      }

      const { data, error } = await query;

      if (error) throw error;
      setTutorials(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addTutorial = async (tutorial: Omit<Tutorial, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('tutorials')
        .insert([tutorial])
        .select()
        .single();

      if (error) throw error;
      await fetchTutorials();
      return data;
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const updateTutorial = async (id: string, updates: Partial<Tutorial>) => {
    try {
      const { error } = await supabase
        .from('tutorials')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
      await fetchTutorials();
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const deleteTutorial = async (id: string) => {
    try {
      const { error } = await supabase
        .from('tutorials')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await fetchTutorials();
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  useEffect(() => {
    fetchTutorials();
  }, [categoryId]);

  return {
    tutorials,
    loading,
    error,
    refetch: fetchTutorials,
    addTutorial,
    updateTutorial,
    deleteTutorial,
  };
}
